import axios from "axios";
import { BACKEND_URL } from "../config";

/** Sync match: PB `itemId` vs catalog row (handles legacy mongo / img filename / csv _id). */
export function favoriteIdMatches(favId, item) {
  if (favId == null || !item) return false;
  const id = String(favId).trim();
  if (!id) return false;
  if (item.__pbFavoriteKeys?.some((k) => String(k).trim() === id)) return true;
  if (id === String(item._id ?? "").trim()) return true;
  if (item.legacyId && id === String(item.legacyId).trim()) return true;
  const il = (item.img || "").trim().toLowerCase();
  if (il) {
    const fl = id.toLowerCase();
    const imgKey = fl.endsWith(".svg") ? fl : `${fl}.svg`;
    if (fl === il || imgKey === il) return true;
  }
  return false;
}

/** PB row id to send to toggleFavorite when removing (legacy id vs csv _id). */
export function findPocketBaseFavoriteId(favIds, item) {
  if (!item) return null;
  if (item.__pbFavoriteKeys?.length) return String(item.__pbFavoriteKeys[0]).trim();
  if (!favIds?.length) return null;
  for (const fid of favIds) {
    if (favoriteIdMatches(fid, item)) return String(fid).trim();
  }
  return null;
}

function isLikelyMongoId(s) {
  return typeof s === "string" && /^[a-f0-9]{24}$/i.test(s.trim());
}

async function fetchLegacyItemName(id) {
  try {
    const res = await axios.get(`${BACKEND_URL}/items/${id}`, { timeout: 10000 });
    const d = res.data;
    const raw =
      d?.name ||
      d?.title ||
      d?.item?.name ||
      (typeof d === "object" && d !== null && d.data?.name);
    return raw ? String(raw).trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * Maps PocketBase favorite itemIds to catalog items (CSV or API-shaped).
 * Supports: current _id, optional item.legacyId, then legacy REST /items/:id name → catalog name.
 */
export async function resolveFavoriteItems(favIds, catalogItems) {
  if (!favIds?.length) return [];
  if (!catalogItems?.length) return [];

  const byId = new Map(catalogItems.map((it) => [String(it._id).trim(), it]));
  const byLegacy = new Map();
  for (const it of catalogItems) {
    if (it.legacyId) {
      byLegacy.set(String(it.legacyId).trim(), it);
    }
  }
  const byName = new Map();
  const byImg = new Map();
  for (const it of catalogItems) {
    const k = (it.name || "").trim().toLowerCase();
    if (k && !byName.has(k)) byName.set(k, it);
    const img = (it.img || "").trim().toLowerCase();
    if (img && !byImg.has(img)) byImg.set(img, it);
  }

  const resolved = new Map();
  const mongoPending = [];

  for (const fid of favIds) {
    const id = String(fid || "").trim();
    if (!id) continue;
    if (byId.has(id)) {
      resolved.set(id, byId.get(id));
      continue;
    }
    if (byLegacy.has(id)) {
      resolved.set(id, byLegacy.get(id));
      continue;
    }
    const idLower = id.toLowerCase();
    const imgKey = idLower.endsWith(".svg") ? idLower : `${idLower}.svg`;
    if (byImg.has(idLower)) {
      resolved.set(id, byImg.get(idLower));
      continue;
    }
    if (byImg.has(imgKey)) {
      resolved.set(id, byImg.get(imgKey));
      continue;
    }
    if (isLikelyMongoId(id)) mongoPending.push(id);
  }

  const uniqueMongo = [...new Set(mongoPending)];
  const nameByMongo = {};
  await Promise.all(
    uniqueMongo.map(async (mid) => {
      const nm = await fetchLegacyItemName(mid);
      if (nm) nameByMongo[mid] = nm;
    })
  );

  for (const mid of uniqueMongo) {
    const nm = nameByMongo[mid];
    if (nm && byName.has(nm)) {
      resolved.set(mid, byName.get(nm));
    }
  }

  const keysForItem = new Map();
  for (const fid of favIds) {
    const id = String(fid || "").trim();
    const base = resolved.get(id);
    if (!base) continue;
    if (!keysForItem.has(base._id)) keysForItem.set(base._id, []);
    keysForItem.get(base._id).push(id);
  }

  const usedItemIds = new Set();
  const out = [];
  for (const fid of favIds) {
    const id = String(fid || "").trim();
    const item = resolved.get(id);
    if (!item || usedItemIds.has(item._id)) continue;
    const keys = keysForItem.get(item._id) || [id];
    out.push({ ...item, __pbFavoriteKeys: keys });
    usedItemIds.add(item._id);
  }
  return out;
}
